import { Component, Input } from '@angular/core';
import { EventResponse, EventCoOrganiserResponse, EventMemberResponse, CoOrganiserAccessLevel } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddCoOrganiserModal } from './components/add-co-organiser-modal/add-co-organiser-modal';
import { AddEventMemberModal } from './components/add-member-modal/add-member-modal';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-event-co-organisers',
  standalone: false,
  templateUrl: './manage-event-co-organisers.html',
  styleUrl: './manage-event-co-organisers.css'
})
export class ManageEventCoOrganisers {
  @Input() event: EventResponse;
  coOrganisers: EventCoOrganiserResponse[] = [];
  members: EventMemberResponse[] = [];
  loading: boolean = false;
  loadingMembers: boolean = false;

  accessLevelOptions: { value: CoOrganiserAccessLevel; label: string; description: string }[] = [
    { value: 'full', label: 'manageEvent.coOrganisers.accessFull', description: 'manageEvent.coOrganisers.accessFullDesc' },
    { value: 'limited', label: 'manageEvent.coOrganisers.accessLimited', description: 'manageEvent.coOrganisers.accessLimitedDesc' },
    { value: 'view', label: 'manageEvent.coOrganisers.accessView', description: 'manageEvent.coOrganisers.accessViewDesc' }
  ];

  constructor(
    private eventsService: EventsService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.getCoOrganisers();
    this.getMembers();
  }

  getCoOrganisers() {
    this.loading = true;
    this.eventsService.getEventCoOrganisers({ event_id: this.event.event_id }).subscribe({
      next: (res: EventCoOrganiserResponse[]) => {
        this.coOrganisers = res;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.coOrganisers = [];
      }
    });
  }

  getMembers() {
    this.loadingMembers = true;
    this.eventsService.getEventMembers({ event_id: this.event.event_id }).subscribe({
      next: (res: EventMemberResponse[]) => {
        this.members = res;
        this.loadingMembers = false;
      },
      error: () => {
        this.loadingMembers = false;
        this.members = [];
      }
    });
  }

  openAddModal() {
    let modalRef = this.modalService.open(AddCoOrganiserModal, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.existingCoOrganiserIds = this.coOrganisers.map(c => c.company_id);
    modalRef.result.then((result) => {
      if (result) {
        this.getCoOrganisers();
      }
    }, () => {});
  }

  openAddMemberModal() {
    let modalRef = this.modalService.open(AddEventMemberModal, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.existingMemberUserIds = this.members.map(m => m.user_id);
    modalRef.result.then((result) => {
      if (result) {
        this.getMembers();
      }
    }, () => {});
  }

  updateAccessLevel(coOrganiser: EventCoOrganiserResponse, newLevel: CoOrganiserAccessLevel) {
    if (coOrganiser.access_level === newLevel) return;

    this.eventsService.updateEventCoOrganiser({
      entry_id: coOrganiser.entry_id,
      access_level: newLevel
    }).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.coOrganisers.updateSuccess'));
        coOrganiser.access_level = newLevel;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.coOrganisers.updateError'));
      }
    });
  }

  updateMemberAccessLevel(member: EventMemberResponse, newLevel: CoOrganiserAccessLevel) {
    if (member.access_level === newLevel) return;

    this.eventsService.updateEventMember({
      member_id: member.member_id,
      access_level: newLevel
    }).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.members.updateSuccess'));
        member.access_level = newLevel;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.members.updateError'));
      }
    });
  }

  removeCoOrganiser(coOrganiser: EventCoOrganiserResponse) {
    let modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.coOrganisers.removeConfirm', {
      company: coOrganiser.company?.company_name || ''
    });
    modalRef.result.then((result) => {
      if (result) {
        this.eventsService.removeEventCoOrganiser({ entry_id: coOrganiser.entry_id }).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.coOrganisers.removeSuccess'));
            this.getCoOrganisers();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.coOrganisers.removeError'));
          }
        });
      }
    }, () => {});
  }

  removeMember(member: EventMemberResponse) {
    let modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.members.removeConfirm', {
      name: member.user?.first_name + ' ' + member.user?.last_name || ''
    });
    modalRef.result.then((result) => {
      if (result) {
        this.eventsService.removeEventMember({ member_id: member.member_id }).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.members.removeSuccess'));
            this.getMembers();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.members.removeError'));
          }
        });
      }
    }, () => {});
  }

  getAccessLevelBadgeClass(level: CoOrganiserAccessLevel): string {
    switch (level) {
      case 'full': return 'badge-light-success';
      case 'limited': return 'badge-light-warning';
      case 'view': return 'badge-light-info';
      default: return 'badge-light-secondary';
    }
  }

  getAccessLevelLabel(level: CoOrganiserAccessLevel): string {
    const option = this.accessLevelOptions.find(o => o.value === level);
    return option ? this.translateService.instant(option.label) : level;
  }

  getMemberInitials(member: EventMemberResponse): string {
    const first = member.user?.first_name?.charAt(0) || '';
    const last = member.user?.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  getMemberFullName(member: EventMemberResponse): string {
    if (member.user) {
      return (member.user.first_name + ' ' + member.user.last_name).trim();
    }
    return member.user_id;
  }
}
